// Display helpers for the stored AI analysis (`stock_analysis`), issue #445.
//
// Everything here is pure so the panel's reading of the model's output can be
// unit-tested without rendering: which dates a "key development" carries, how
// old the analysis is, and which of its 1–10 scores are real numbers.

import { MONTHS } from './dateLabels';
import { parseEventDate } from './earningsElapsed';
import { finiteOrNull } from './numbers';
import { nameMentions } from '../shared/companyName';

// Full names, three-letter forms and "Sept" (the model writes all three).
const MONTH_NUMBER = MONTHS.reduce((acc, short, i) => {
  acc[short.toLowerCase()] = i + 1;
  return acc;
}, { sept: 9 });
['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august',
 'september', 'october', 'november', 'december'].forEach((name, i) => { MONTH_NUMBER[name] = i + 1; });

const MONTH_WORD = '([A-Za-z]{3,9})\\.?';
// The model emits U+2011 (non-breaking hyphen) as often as an ASCII one —
// CRWD's developments read "(2026‑09‑15)" — so accept every dash variant.
const DASH = '[-\\u2010-\\u2015]';
const LEAD_SEPARATOR = `\\s*(?:${DASH}|:)\\s*`;
const YEAR = '((?:19|20)\\d{2})';

// Ordered: the first pattern that matches wins.
const DATE_PATTERNS = [
  // "… record highs (2026‑09‑14)."
  { re: new RegExp(`\\s*\\(${YEAR}${DASH}(\\d{2})${DASH}(\\d{2})\\)(\\.?)\\s*$`),
    read: (m) => ({ year: +m[1], month: +m[2], day: +m[3], rest: (t) => t.slice(0, m.index) + m[4] }) },
  // "… to 12 (2024)."
  { re: new RegExp(`\\s*\\(${YEAR}\\)(\\.?)\\s*$`),
    read: (m) => ({ year: +m[1], month: null, day: null, rest: (t) => t.slice(0, m.index) + m[2] }) },
  // "Sept 29, 2026 – Completed US IPO …"
  { re: new RegExp(`^${MONTH_WORD}\\s+(\\d{1,2}),?\\s+${YEAR}${LEAD_SEPARATOR}`),
    read: (m) => ({ monthWord: m[1], year: +m[3], day: +m[2], rest: (t) => t.slice(m[0].length) }) },
  // "June 2026: Company changed name …"
  { re: new RegExp(`^${MONTH_WORD}\\s+${YEAR}${LEAD_SEPARATOR}`),
    read: (m) => ({ monthWord: m[1], year: +m[2], day: null, rest: (t) => t.slice(m[0].length) }) },
];

const validDate = ({ year, month, day }) => {
  if (month == null) return day == null;
  if (month < 1 || month > 12) return false;
  return day == null || (day >= 1 && day <= 31);
};

const dateLabel = ({ year, month, day }) => {
  if (month == null) return String(year);
  if (day == null) return `${MONTHS[month - 1]} ${year}`;
  return `${MONTHS[month - 1]} ${day}, ${year}`;
};

// One key development → its sentence and, when the model wrote one, the date it
// happened. The date is lifted OUT of the sentence so it can be shown as a chip
// instead of buried in the prose. Anything unrecognised is left exactly as
// written with `date: null` — a guessed date would be worse than none.
export function splitDevelopmentDate(item) {
  const text = typeof item === 'string' ? item.trim() : '';
  if (!text) return null;

  for (const { re, read } of DATE_PATTERNS) {
    const m = text.match(re);
    if (!m) continue;
    const parsed = read(m);
    const month = parsed.monthWord !== undefined
      ? MONTH_NUMBER[parsed.monthWord.toLowerCase()] ?? null
      : parsed.month;
    // A leading word that is not a month ("Launch 2026: …") is not a date.
    if (parsed.monthWord !== undefined && month == null) continue;
    const date = { year: parsed.year, month, day: parsed.day };
    if (!validDate(date)) continue;

    const rest = parsed.rest(text).trim();
    if (!rest) return { text, date: null };
    return {
      text: rest.charAt(0).toUpperCase() + rest.slice(1),
      date: {
        label: dateLabel(date),
        sortKey: date.year * 10000 + (date.month || 0) * 100 + (date.day || 0),
      },
    };
  }
  return { text, date: null };
}

// The list as rendered: dated items first, newest first; undated items after,
// in the order the model wrote them (that order is the only ranking they have).
export function orderDevelopments(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map(splitDevelopmentDate)
    .filter(Boolean)
    .map((entry, index) => ({ ...entry, index }))
    .sort((a, b) => {
      if (a.date && b.date) return b.date.sortKey - a.date.sortKey || a.index - b.index;
      if (a.date) return -1;
      if (b.date) return 1;
      return a.index - b.index;
    });
}

// Plain list items (risk factors): strings only, blanks dropped.
export function cleanItems(items) {
  if (!Array.isArray(items)) return [];
  return items
    .filter((item) => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean);
}

// When the analysis was written. `updated_at` is D1's datetime('now') — UTC,
// space-separated, no zone — so it is read by its date PREFIX: `new Date()` on
// it would parse LOCAL time, and the old footer's toISOString() then printed
// the next day for any evening write viewed west of Greenwich.
export function analysisAsOf(updatedAt, now = new Date()) {
  const written = parseEventDate(updatedAt);
  if (!written) return null;
  const { year, month, day } = written;

  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const ageDays = Math.max(0, Math.round((today - written.utc) / 86400000));

  let age;
  if (ageDays === 0) age = 'today';
  else if (ageDays === 1) age = 'yesterday';
  else if (ageDays < 30) age = `${ageDays} days ago`;
  else if (ageDays < 365) {
    const months = Math.floor(ageDays / 30);
    age = `${months} month${months === 1 ? '' : 's'} ago`;
  } else {
    const years = Math.floor(ageDays / 365);
    age = `${years} year${years === 1 ? '' : 's'} ago`;
  }

  return {
    label: `${MONTHS[month - 1]} ${day}, ${year}`,
    isoDate: updatedAt.slice(0, 10),
    ageDays,
    age,
  };
}

// The 1–10 scores, in display order. For the first five a HIGH score is
// favourable ("Competitive position" rather than "competition", so 7 reads the
// right way round). `speculation` (issue #446) runs the OTHER way — 10 means the
// price rests almost entirely on hype or narrative — so it is flagged
// `higherIsWorse` and drawn apart from the rest. Analyses written before #446
// carry no speculation score; it is then simply absent.
const AI_SCORE_FIELDS = [
  { key: 'financials', label: 'Financials' },
  { key: 'growth_prospects', label: 'Growth prospects' },
  { key: 'competition', label: 'Competitive position' },
  { key: 'management_quality', label: 'Management' },
  { key: 'valuation', label: 'Valuation' },
  { key: 'speculation', label: 'Speculation / hype', higherIsWorse: true },
];

// A missing score must not draw as a confident empty bar, and placeholder text
// or an off-scale number is not a score at all.
const scoreValue = (raw) => {
  const value = finiteOrNull(raw);
  return value !== null && value >= 0 && value <= 10 ? value : null;
};

export function aiScores(summaryRating) {
  if (!summaryRating || typeof summaryRating !== 'object') return [];
  return AI_SCORE_FIELDS
    .map(({ key, label, higherIsWorse = false }) => ({
      key, label, higherIsWorse, value: scoreValue(summaryRating[key]),
    }))
    .filter((score) => score.value !== null);
}

// The speculation / hype score alone (issue #446), or null when the analysis
// predates it or the value isn't a usable score.
export const aiSpeculation = (summaryRating) =>
  aiScores(summaryRating).find((score) => score.key === 'speculation') ?? null;

// The model's Strong Buy … Strong Sell call, on the same 1 (most bullish) – 5
// scale as Yahoo's analyst consensus so both share one colour ladder.
const RECOMMENDATIONS = ['Strong Buy', 'Buy', 'Hold', 'Sell', 'Strong Sell'];

export function aiRecommendation(rating) {
  if (typeof rating !== 'string') return null;
  const wanted = rating.trim().toLowerCase();
  const index = RECOMMENDATIONS.findIndex((r) => r.toLowerCase() === wanted);
  return index === -1 ? null : { label: RECOMMENDATIONS[index], rank: index + 1 };
}

// ── Competitors (issue #445) ────────────────────────────────────────────────
// The AI names ~3 competitors per analysis, with a ticker on 87% of them. A
// ticker is only a CLAIM: 68% resolve to a stock we track, and some of those
// are a different company than the one named (stale or hallucinated symbols).

const MAX_COMPETITORS = 8;
const TICKER_SHAPE = /^[A-Z][A-Z0-9.-]{0,9}$/;

// The AI's competitor list, cleaned: name required, ticker upper-cased and only
// kept when it looks like one, the subject itself and repeats dropped.
export function competitorEntries(competitors, subjectSymbol) {
  if (!Array.isArray(competitors)) return [];
  const subject = String(subjectSymbol || '').toUpperCase();
  const seen = new Set();
  const entries = [];
  for (const competitor of competitors) {
    const name = typeof competitor?.name === 'string' ? competitor.name.trim() : '';
    if (!name) continue;
    const raw = typeof competitor.ticker === 'string' ? competitor.ticker.trim().toUpperCase() : '';
    if (raw && raw === subject) continue; // the company listed as its own competitor
    const ticker = TICKER_SHAPE.test(raw) ? raw : null;
    const key = ticker || name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    entries.push({
      name,
      ticker,
      differentiation: typeof competitor.differentiation === 'string' ? competitor.differentiation.trim() : '',
    });
    if (entries.length === MAX_COMPETITORS) break;
  }
  return entries;
}

// Attach a tracked stock's row to each entry ONLY when the ticker resolves AND
// our stored name names the company the AI wrote (`nameMentions`). Anything
// else stays name-only: no data beats another company's data.
export function matchCompetitors(entries, profiles) {
  const bySymbol = new Map((Array.isArray(profiles) ? profiles : [])
    .filter((profile) => profile?.tickerSymbol)
    .map((profile) => [String(profile.tickerSymbol).toUpperCase(), profile]));
  return entries.map((entry) => {
    const profile = entry.ticker ? bySymbol.get(entry.ticker) : null;
    const matched = !!profile && (nameMentions(profile.name, entry.name) || nameMentions(profile.longName, entry.name));
    return { ...entry, stock: matched ? profile : null };
  });
}
